import { Controller } from "@hotwired/stimulus"

export default class extends Controller {
    static targets = ["form", "checkbox", "filter", "container", "listIcon", "gridIcon", "filterBtn", "chipsContainer", "searchInput", "warning"]
    
    lastScrollTop = 0
    visibleItems = 9
    listViewVisibleItems = 15
    currentValue = ""

    initialize() {
        const submitButton = document.getElementById("submit-button");
        if (submitButton) {
            submitButton.click();
        }
        if (this.hasChipsContainerTarget) {
            this.chipsContainerTarget.addEventListener("click", this.handleChipClick.bind(this));
        }

    }

    reset(event) {
        event.preventDefault()
        this.resetCheckboxes()
        document.getElementById("searchCareers").value = '';
        document.getElementById("searchPillars").value = '';
        this.resetRangeControllers()
    }

    resetCheckboxes() {
        document.querySelectorAll('[data-controller="checkbox"]').forEach(element => {
            const checkboxController = this.application.getControllerForElementAndIdentifier(element, 'checkbox')
            if (checkboxController && typeof checkboxController.clearAll === 'function') {
                checkboxController.clearAll()
            }
        })
    }

    getRangeControllers() {
        return Array.from(document.querySelectorAll('[data-controller="range"], [data-controller="yearRange"]'))
            .map(element => {
                const controllerName = element.getAttribute('data-controller');
                return this.application.getControllerForElementAndIdentifier(element, controllerName);
            })
            .filter(controller => controller !== null);
    }

    resetRangeControllers() {
        this.getRangeControllers().forEach(controller => {
            if (typeof controller.resetSlider === 'function') {
                controller.resetSlider();
            }
        });
    }

    formChanged(event) {
        event.preventDefault();
        const isMobile = window.innerWidth < 768;
        if (isMobile && event.target.name !== "searchTerm") {
            return;
        }
        if (!event.target.hasAttribute('data-scrollTrigger')) {
            document.getElementById('resultsNumber').value = 12;
        }
        
        clearTimeout(this.formTimeout);
        this.formTimeout = setTimeout(() => {
            this.saveFiltersToUrl();
            document.getElementById("submit-button").click();
        }, 100);
    }

    preventEnter(event) {
        if (event.key === 'Enter' && this.currentValue.length === 1){
            event.preventDefault();
        }
    }

    search(event) { 
        const searchValue = this.searchInputTarget.value.trim();
        this.currentValue = searchValue;
        if (searchValue.length === 1 ) {
            this.warningTarget.classList.remove('hidden');
            return;
        } else {
            this.warningTarget.classList.add('hidden');
        }
        clearTimeout(this.searchTimeout);
        this.searchTimeout = setTimeout(() => {
            this.formChanged(event);
        }, 300);
    }

    clear() {
        document.getElementById("searchCareers").value = '';
        this.warningTarget.classList.add('hidden');
        this.saveFiltersToUrl();
        document.getElementById("submit-button").click();
    }

    switchView() {
        const className = "list-view";
        const activeClasses = ["bg-blue", "text-white"]
        const viewModeInput = document.getElementById("viewMode");

        if (!this.containerTarget.classList.contains(className)) {
            this.containerTarget.classList.add(className);
            this.listIconTarget.classList.add(...activeClasses);
            this.gridIconTarget.classList.remove(...activeClasses);
            viewModeInput.value = "list";
        } else {
            this.containerTarget.classList.remove(className);
            this.listIconTarget.classList.remove(...activeClasses);
            this.gridIconTarget.classList.add(...activeClasses);
            viewModeInput.value = "grid";
        }
    }

    saveFiltersToUrl() {
        const form = document.getElementById('filter-form');
        const params = [];
        form.querySelectorAll('input').forEach(input => {
            if ((input.type === 'checkbox' && input.checked) || 
                (input.type === 'number' && input.value !== '')) {
                const name = input.name;
                const value = input.value;
                params.push(`${encodeURIComponent(name)}=${encodeURIComponent(value)}`);
            }
        });
        const searchInput = document.getElementById('searchCareers');
        if (searchInput?.value) {
            params.push(`searchTerm=${encodeURIComponent(searchInput.value)}`);
        }
        const queryString = params.join('&');
        if (`?${queryString}` !== window.location.search) {
            window.history.replaceState(null, '', `?${queryString}`);
        }
    }

    toggleFilterModal() {
        const filterModal = document.getElementById("filterModal");
        if (filterModal) {
            filterModal.classList.remove("hidden");
            filterModal.classList.add("lg:block");
            document.body.style.overflow = "hidden";
            const navBar = document.querySelector('nav');
            if (navBar) {
                navBar.classList.add("hidden");
            }
            const mainContent = document.querySelector('[data-controller="careerSearchTool"]');
            if (mainContent) {
                Array.from(mainContent.children).forEach(child => {
                    if (!child.contains(filterModal)) {
                        child.classList.add("hidden", "lg:block");
                    }
                });
            }
        }
    }

    loadFiltersFromUrl() {
        const urlParams = new URLSearchParams(window.location.search);
        const form = document.getElementById('filter-form');
        
        if (!form) return;
        
        this.resetCheckboxes();
        this.resetRangeControllers();
        this.restoreFormInputsFromUrl(form, urlParams);
        this.updateRangeControllers();
    }

    restoreFormInputsFromUrl(form, urlParams) {
        const inputHandlers = {
            'checkbox': (input, value) => { input.checked = (input.value === value); },
            'number': (input, value) => { input.value = value; }
        };

        form.querySelectorAll('input').forEach(input => {
            const paramValue = urlParams.get(input.name);
            if (!paramValue) return;

            const handler = inputHandlers[input.type];
            if (handler) {
                handler(input, paramValue);
            }
        });
    }

    updateRangeControllers() {
        this.getRangeControllers().forEach(controller => {
            if (!controller.initializeHandleInputs) return;
            
            controller.initializeHandleInputs();
            
            if (controller.dispatchChangeEvent) {
                controller.dispatchChangeEvent();
            }
            
            if (controller.updateClearButtonVisibility) {
                controller.updateClearButtonVisibility();
            }
        });
    }

    closeFilterModal() {
        const filterModal = document.getElementById("filterModal");
        if (filterModal) {
            this.loadFiltersFromUrl();

            filterModal.classList.add("hidden");
            document.body.style.overflow = "auto";
            const navBar = document.querySelector('nav');
            if (navBar) {
                navBar.classList.remove("hidden");
            }
            const mainContent = document.querySelector('[data-controller="careerSearchTool"]');
            if (mainContent) {
                Array.from(mainContent.children).forEach(child => {
                    child.classList.remove("hidden", "lg:block");
                });
            }
        }
    }

    applyFilters() {
        this.saveFiltersToUrl();
        document.getElementById("submit-button").click();        
        this.closeFilterModal();
    }

    handleChipClick(e) {
        const chip = e.target.closest(".chip");
        const checkbox = this.element.querySelector(`input[name='pillar'][value='${chip.dataset['value']}']`);
        checkbox.checked = false;
        const event = new Event('change');
        checkbox.dispatchEvent(event);
        chip.remove();
    }
}